/* ============================================================================
   API client — the single place the frontend talks to the backend.

   In development, requests go to /api and Vite proxies them to the backend on
   port 4000 (see vite.config.js), so there is no CORS setup to do locally.
   In production, set VITE_API_URL to the deployed API origin.

   If VITE_API_URL is blank AND the backend is not running, submitEnquiry
   resolves with { delivered: false } instead of throwing. The contact form
   still shows its success state, so the marketing site can be deployed and
   demoed before the backend exists.
   ========================================================================== */

const BASE = (import.meta.env?.VITE_API_URL ?? "").replace(/\/$/, "");

const url = (path) => `${BASE}${path}`;

async function request(path, { method = "GET", body, signal } = {}) {
  const res = await fetch(url(path), {
    method,
    signal,
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  const data = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const error = new Error(data?.message || `Request failed (${res.status})`);
    error.status = res.status;
    error.details = data;
    throw error;
  }
  return data;
}

/**
 * Send a quotation enquiry from the contact form.
 * @param {{name:string, company?:string, email:string, phone?:string, service:string, message:string}} enquiry
 * @returns {Promise<{delivered:boolean, reference?:string}>}
 */
export async function submitEnquiry(enquiry) {
  try {
    const data = await request("/api/enquiries", { method: "POST", body: enquiry });
    return { delivered: true, reference: data?.reference };
  } catch (err) {
    // A validation rejection from the backend is a real error — surface it.
    if (err.status && err.status >= 400 && err.status < 500) throw err;
    // Network failure or backend down: don't block the visitor.
    console.warn("[a4] enquiry not delivered to the API:", err.message);
    return { delivered: false };
  }
}

/** Simple readiness check, useful for status pages and deploy smoke tests. */
export const checkHealth = () => request("/api/health");

export default { submitEnquiry, checkHealth };
